import { map, tap } from 'rxjs/operators';
import { ApiService } from '@hub-services/api';
import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UA } from './ua.interface';

@Injectable({ providedIn: 'root' })
export class UasWorkflowService {
  path: string = '/api';
  constructor(
    private apiService: ApiService,
    private datePipe: DatePipe,
  ) { }

  getLabel(type: string): string {
    if (type === 'utas') {
      return 'Attribute';
    }

    return 'Visual';
  }

  draftUa(ua: any, type: string, pathType: string = type) {
    return this.apiService
      .post(`${this.path}/${type}/${ua.id}/status_transitions`, {
        data: {
          status: 'draft',
        },
      })
      .pipe(
        map(() => {
          let updatedUa = this.prepUaForUpdate(ua, type, 'draft');
          updatedUa.moved_title = `${this.getLabel(type)} ${ua.id} has been updated to "Draft" from "${this.getOldUaStatus(ua)}".`;
          updatedUa.moved_description = `This ${this.getLabel(
            type,
          )} will hang out in "Draft" until published.`;
          return updatedUa;
        }),
      );
  }

  submitUa(ua: any, type: string, pathType: string = type) {
    return this.apiService.post(`${this.path}/${type}/${ua.id}/submit`, {}).pipe(
      map(() => {
        let updatedUa = this.prepUaForUpdate(ua, type, 'pending');
        updatedUa.moved_title = `${this.getLabel(type)} ${ua.id} has been updated to "In review" from "${this.getOldUaStatus(ua)}".`;
        updatedUa.moved_description = `This ${this.getLabel(
          type,
        )} will hang out "In review" until published.`;
        return updatedUa;
      }),
    );
  }

  publishUa(ua: any, type: string, pathType: string = type) {
    return this.apiService.post<UA>(`${this.path}/${type}/${ua.id}/publish`, {}).pipe(
      map((response) => {
        let updatedUa = this.prepUaForUpdate(ua, type, 'published');
        let suffix: string = `and is now available everywhere`;
        if (response.is_pending) {
          suffix = `but will wait "In review" until ${this.datePipe.transform(response.starts_on, 'MMMM d, yyyy')}`;
        }
        updatedUa.moved_title = `${this.getLabel(type)} ${ua.id} has been updated to "Published" from "${this.getOldUaStatus(ua)}".`;
        updatedUa.moved_description = `This ${this.getLabel(type)} has been "Published" ${suffix}.`;
        return updatedUa;
      }),
    );
  }

  disableUa(ua: any, type: string, pathType: string = type) {
    return this.apiService.post(`${this.path}/${type}/${ua.id}/disable`, {}).pipe(
      map(() => {
        let updatedUa = this.prepUaForUpdate(ua, type, 'disabled');
        updatedUa.moved_title = `${this.getLabel(type)} ${ua.id} has been updated to "Disabled" from "${this.getOldUaStatus(ua)}".`;
        updatedUa.moved_description = `This ${this.getLabel(
          type,
        )} will remain "Disabled" until you enable it.`;
        return updatedUa;
      }),
    );
  }

  enableUa(ua: any, type: string, pathType: string = type) {
    return this.apiService.post(`${this.path}/${type}/${ua.id}/enable`, {}).pipe(
      map(() => {
        let updatedUa = this.prepUaForUpdate(ua, type, 'published');
        updatedUa.moved_title = `${this.getLabel(type)} ${ua.id
          } has been updated to "Published" from "${this.getOldUaStatus(ua)}".`;
        updatedUa.moved_description = `This ${this.getLabel(
          type,
        )} has been "Published" and is now available everywhere.`;
        return updatedUa;
      }),
    );
  }

  cloneUa(ua: any, type: string, partnerId: any, pathType: string = type) {
    return this.apiService
      .post<UA>(`${this.path}/airlines/${partnerId}/${type}`, {
        data: {
          source_upa_id: ua.id,
        },
      })
      .pipe(
        map((response) => {
          return `/${pathType}/${response.id}/basics`;
        }),
      );
  }

  deleteUa(ua: any, type: string) {
    return this.apiService.delete(`${this.path}/${type}/${ua.id}`);
  }

  prepUaForUpdate(ua: any, type: string, action: string) {
    let clonedUa = { ...ua }
    clonedUa.moved_path = `/${type}/${action}`;
    clonedUa.is_moving = false;
    clonedUa.is_moved = true;
    return clonedUa;
  }

  getOldUaStatus(ua: any): string {
    if (ua.is_draft) {
      return 'Draft'
    } else if (ua.in_review) {
      return 'In review'
    } else if (ua.is_published) {
      return 'Published'
    } else if (ua.is_disabled) {
      return 'Disabled'
    }
    return '';
  }
}
